import React from "react";
import { Container, Row, Col, Card, Badge } from "react-bootstrap";
import { Helmet } from "react-helmet";
import web5 from "../img/custom.jpg";

const Custom = () => {
  const heroWrap = {
    borderRadius: 18,
    border: "1px solid #e8eef7",
    boxShadow: "0 18px 44px rgba(15, 23, 42, 0.10)",
    overflow: "hidden",
    position: "relative",
    backgroundImage: `url(${web5})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  const heroOverlay = {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(90deg, rgba(2,6,23,0.85) 0%, rgba(2,6,23,0.60) 45%, rgba(2,6,23,0.20) 100%)",
  };

  const heroContent = {
    position: "relative",
    padding: "38px 28px",
    maxWidth: 900,
    color: "#fff",
  };

  const pill = {
    display: "inline-block",
    padding: "8px 14px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.25)",
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: "0.4px",
    marginBottom: 12,
  };

  const sectionCard = {
    borderRadius: 16,
    border: "1px solid #eef2f7",
    boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
  };

  const sectionTitle = {
    fontWeight: 900,
    color: "#0f172a",
    marginBottom: 8,
  };

  const divider = {
    width: 70,
    height: 4,
    borderRadius: 12,
    background: "#ff6a00",
    marginBottom: 16,
  };

  const para = {
    lineHeight: 1.9,
    color: "#1f2937",
    fontSize: "1.02rem",
    textAlign: "justify",
    marginBottom: 14,
  };

  return (
    <>
      {/* ✅ SEO */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          Custom Software Development Company in India | EBS Software Solution
        </title>
        <meta
          name="description"
          content="EBS Software Solution provides customized software development services in India. We build scalable, secure and cost-effective business software solutions tailored to your exact operational needs."
        />
        <meta
          name="keywords"
          content="Custom Software Development India, Customized Business Software, ERP Customization, Software Development Company Siliguri, Enterprise Software Solutions"
        />
      </Helmet>

      {/* ✅ HERO */}
      <Container className="py-3">
        <div style={heroWrap}>
          <div style={heroOverlay} />
          <div style={heroContent}>
            <div style={pill}>CUSTOM SOFTWARE • ENTERPRISE SOLUTIONS</div>

            <h1 style={{ fontWeight: 900, fontSize: "clamp(32px,4vw,44px)" }}>
              Customized Software Development
            </h1>

            <p style={{ maxWidth: 720, lineHeight: 1.7, fontWeight: 600 }}>
              Tailor-made business software engineered to match your exact
              operational workflow, industry requirements and growth goals.
            </p>

            <div className="d-flex gap-2 flex-wrap mt-2">
              <Badge bg="light" text="dark" style={{ fontWeight: 800, padding: "10px 12px" }}>
                ERP Customization
              </Badge>
              <Badge bg="light" text="dark" style={{ fontWeight: 800, padding: "10px 12px" }}>
                Web Applications
              </Badge>
              <Badge bg="light" text="dark" style={{ fontWeight: 800, padding: "10px 12px" }}>
                Industry-Specific Solutions
              </Badge>
            </div>
          </div>
        </div>
      </Container>

      {/* ✅ CONTENT */}
      <Container className="py-4">
        <Row className="g-4">
          <Col lg={12}>
            <Card style={sectionCard}>
              <Card.Body style={{ padding: 22 }}>
                <h2 style={sectionTitle}>Why Custom Software?</h2>
                <div style={divider} />

                <p style={para}>
                  EBS Software Solution is a quality-focused software development
                  company delivering customized software solutions using proven
                  development methodologies. We help organizations overcome the
                  limitations of off-the-shelf software by designing systems
                  that align perfectly with their business processes.
                </p>

                <p style={para}>
                  Most standard software packages can only fulfill around 60–70%
                  of real-world business requirements. Either they lack critical
                  features or include unnecessary functionality that increases
                  cost and complexity. Custom software eliminates these gaps by
                  delivering exactly what your business needs—nothing more,
                  nothing less.
                </p>

                <p style={para}>
                  Our experienced engineers analyze your domain, workflows, and
                  future expansion plans before developing a solution. This
                  ensures long-term scalability, security, and reliability while
                  maintaining cost-effectiveness.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ✅ HIGHLIGHTS */}
        <Row className="mt-4 g-4">
          {[
            {
              title: "Tailored to Your Business",
              text:
                "Every feature is designed specifically for your operational needs, eliminating inefficiencies.",
            },
            {
              title: "Scalable & Future-Ready",
              text:
                "Solutions designed to grow with your business and adapt to changing requirements.",
            },
            {
              title: "Cost-Effective Development",
              text:
                "Pay only for what you need—no unnecessary modules or license costs.",
            },
            {
              title: "Reliable & Secure",
              text:
                "Robust testing and structured development ensure stability and data security.",
            },
            {
              title: "Transparent Communication",
              text:
                "Regular progress updates, reports, and feedback cycles throughout the project.",
            },
            {
              title: "Post-Delivery Support",
              text:
                "Ongoing maintenance, upgrades, training and technical support.",
            },
          ].map((item, idx) => (
            <Col key={idx} md={6} lg={4}>
              <Card style={sectionCard} className="h-100">
                <Card.Body style={{ padding: 18 }}>
                  <h5 style={{ fontWeight: 900, color: "#0f172a" }}>{item.title}</h5>
                  <p style={{ marginBottom: 0, color: "#334155", lineHeight: 1.7 }}>
                    {item.text}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ✅ CTA */}
        <Row className="mt-4">
          <Col lg={12}>
            <Card
              style={{
                ...sectionCard,
                background: "linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%)",
              }}
            >
              <Card.Body style={{ padding: 20 }}>
                <h4 style={{ fontWeight: 900, color: "#0f172a" }}>
                  Need a Custom Software Solution?
                </h4>
                <p style={{ ...para, marginBottom: 0 }}>
                  Contact our team to discuss your requirements. We provide
                  affordable, professional and scalable customized software
                  solutions for businesses across India.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Custom;
